// The menu layer's layout (ui-qol §4): at ≥ 700 px a fixed 985 × 600 design box with three
// 325 px columns (gap 5 px), scaled by `menuScale` = min(1, W/1600, H/890) and centred at 45 %
// height; below 700 px one scrolling column (play card, modes, money, how-to).
// - Left: "How to play" (180) and "Open source" (117) cards, and the round "?" help button at
//   the column's bottom-right (opens the Help modal).
// - Centre: the play card, or the round results / cash-out card in their place.
// - Right: the mode card (180) and, while the server offers money arenas, the money card (in the
//   space agar uses for its party card).
// - The footer tab at the top-right of the page: "Source code" (when /api/info has a sourceUrl)
//   and the client version.
// On the results screens the side columns are empty (agar keeps the party card there; we have no
// party card yet).
//
// Consumer: app.ts.

import type { UiClickSource } from '../../events/bus.js';
import { h } from '../dom.js';
import { t } from '../i18n/t.js';
import { helpIcon } from '../icons.js';

/** The design-box scale (ui-qol §1): min(1, W/1600, H/890). */
export function menuScale(cssWidth: number, cssHeight: number): number {
  if (!(cssWidth > 0) || !(cssHeight > 0)) return 1;
  return Math.min(1, cssWidth / 1600, cssHeight / 890);
}

/** Below this width the menu is one scrolling column (ui-qol §4.4). */
export const ONE_COLUMN_BELOW_PX = 700;

export interface MenuLayoutParts {
  readonly window: Window;
  /** Centre column screens (play card first). */
  readonly center: readonly HTMLElement[];
  /** Right column cards (mode card, money card). */
  readonly right: readonly HTMLElement[];
  onHelp(): void;
  playClick?(source: UiClickSource): void;
  readonly version: string;
  /** Touch device (coarse pointer): the how-to card uses the touch wording. Absent = false. */
  readonly touch?: boolean;
}

export interface MenuLayout {
  readonly el: HTMLElement;
  /** The footer tab (mount it in #menu after `el`). */
  readonly footer: HTMLElement;
  /** 'home' shows the side columns; 'results' empties them. */
  setScreen(screen: 'home' | 'results'): void;
  setSourceUrl(url: string): void;
  dispose(): void;
}

/** The "How to play" card's three lines; touch devices get the touch wording (like the play card). */
export function howToLines(touch: boolean): [string, string, string] {
  return touch
    ? [t('howto.move.touch'), t('howto.split.touch'), t('howto.shoot.touch')]
    : [t('howto.move'), t('howto.split'), t('howto.shoot')];
}

function howToCard(touch: boolean): HTMLElement {
  return h(
    'section',
    { class: 'card side-card howto-card' },
    h('h2', { class: 'card-title' }, t('howto.title')),
    h('ul', { class: 'howto-list' }, ...howToLines(touch).map((line) => h('li', null, line))),
  );
}

export function createMenuLayout(parts: MenuLayoutParts): MenuLayout {
  const win = parts.window;
  const sourceLink = h(
    'a',
    { class: 'opensource-link', target: '_blank', rel: 'noopener noreferrer', hidden: true },
    t('opensource.link'),
  );
  const openSource = h(
    'section',
    { class: 'card side-card opensource-card' },
    h('h2', { class: 'card-title' }, t('opensource.title')),
    h('p', { class: 'opensource-body' }, t('opensource.body')),
    sourceLink,
  );
  const helpBtn = h(
    'button',
    {
      type: 'button',
      class: 'help-btn',
      'aria-label': t('menu.help'),
      title: t('menu.help'),
      onClick: () => {
        parts.playClick?.('help');
        parts.onHelp();
      },
    },
    helpIcon(),
  );
  const left = h(
    'div',
    { class: 'menu-col menu-left' },
    howToCard(parts.touch ?? false),
    openSource,
    helpBtn,
  );
  const center = h('div', { class: 'menu-col menu-center' }, ...parts.center);
  const right = h('div', { class: 'menu-col menu-right' }, ...parts.right);
  const box = h('div', { class: 'menu-box' }, left, center, right);
  const el = h('div', { class: 'menu-stage' }, box);

  const footerSource = h(
    'a',
    { class: 'footer-link', target: '_blank', rel: 'noopener noreferrer', hidden: true },
    t('footer.source'),
  );
  const footerSep = h('span', { class: 'footer-sep', hidden: true }, ' | ');
  const footer = h(
    'nav',
    { class: 'menu-footer', 'aria-label': 'ball.io' },
    footerSource,
    footerSep,
    h('span', { class: 'footer-version' }, t('footer.version', { version: parts.version })),
  );

  const applyScale = (): void => {
    const s = menuScale(win.innerWidth, win.innerHeight);
    win.document.documentElement.style.setProperty('--menu-scale', String(s));
  };
  applyScale();
  win.addEventListener('resize', applyScale, { passive: true });

  return {
    el,
    footer,
    setScreen(screen): void {
      el.dataset['screen'] = screen;
      // Hidden but still laid out, so the centre column never moves.
      left.classList.toggle('is-away', screen !== 'home');
      for (const card of right.children) {
        if (card instanceof HTMLElement) card.classList.toggle('is-away', screen !== 'home');
      }
    },
    setSourceUrl(url: string): void {
      const ok = /^https?:\/\//i.test(url);
      for (const a of [sourceLink, footerSource]) {
        a.hidden = !ok;
        if (ok) a.setAttribute('href', url);
      }
      footerSep.hidden = !ok;
    },
    dispose(): void {
      win.removeEventListener('resize', applyScale);
    },
  };
}
